import type { EventSummary } from "@project-x/api-clients";
import { formatSwapDateCapitalized } from "@/utils/timezone";

/**
 * Venue lines shown on consumer event showcase cards.
 * First line is the venue name; second line is province/city when available.
 */
export interface EventCardVenueParts {
  /** Venue / location name for the pin row. */
  name: string;
  /** Province or city for the bullet row; empty when unavailable. */
  region: string;
}

/**
 * Formats an event start date for showcase cards using the same compact shape as
 * swap cards (e.g. "Mié, 22 Jul 2026"), including year so cross-year events stay clear.
 * Card CSS uppercases the result (e.g. "MIÉ, 22 JUL 2026").
 */
export function formatEventCardDate(value: string, locale: string): string {
  return formatSwapDateCapitalized(value, locale);
}

/**
 * Splits venue fields into card display parts: name on the pin row, state/city on the bullet row.
 * Prefers state over city for the region line to match prior card copy.
 */
export function getEventCardVenueParts(event: EventSummary): EventCardVenueParts | null {
  const name = event.venue?.name?.trim() ?? "";
  const region = (event.venue?.state ?? event.venue?.city)?.trim() ?? "";
  if (!name && !region) {
    return null;
  }
  return { name, region };
}
